import { ArrowRight, CheckCircle2, FileSearch, FileText, Rocket, Upload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type DemoStep = {
  num: number;
  icon: typeof Upload;
  title: string;
  desc: string;
  color: string;
  micro: string;
};

export function useDemoSteps(
  t: (key: string, options?: Record<string, unknown>) => string,
): DemoStep[] {
  return useMemo(
    () => [
      {
        num: 1,
        icon: Upload,
        title: t('home.demoSteps.step1Title', 'Upload Your Resume'),
        desc: t(
          'home.demoSteps.step1Desc',
          'AI scans your experience, skills, and target roles in under 5 seconds — no manual data entry.',
        ),
        color: '#6366f1',
        micro: '📄',
      },
      {
        num: 2,
        icon: FileSearch,
        title: t('home.demoSteps.step2Title', 'Get AI-Matched Jobs'),
        desc: t(
          'home.demoSteps.step2Desc',
          'Scans Naukri, LinkedIn, Peerlist, and more to surface roles where your skills rank highest.',
        ),
        color: '#f59e0b',
        micro: '🔍',
      },
      {
        num: 3,
        icon: FileText,
        title: t('home.demoSteps.step3Title', 'Application packs ready'),
        desc: t(
          'home.demoSteps.step3Desc',
          'For every job, get a JD-tailored resume, cover letter, and outreach message — ready for your review.',
        ),
        color: '#22c55e',
        micro: '✍️',
      },
      {
        num: 4,
        icon: Rocket,
        title: t('home.demoSteps.step4Title', 'You apply — we track & prep'),
        desc: t(
          'home.demoSteps.step4Desc',
          "You submit when you're ready. GoGrabJob tracks your pipeline and serves role-specific interview prep.",
        ),
        color: '#06b6d4',
        micro: '🚀',
      },
    ],
    [t],
  );
}

function StepConnector({ orientation }: { orientation: 'horizontal' | 'vertical' }) {
  if (orientation === 'vertical') {
    return (
      <div className="flex justify-center py-1 sm:hidden" aria-hidden>
        <svg width="20" height="16" viewBox="0 0 20 16" fill="none" className="text-stone-300">
          <path
            d="M10 0v12M4 8l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <div
      className="home-hiw-stepper__connector hidden shrink-0 sm:flex sm:items-center sm:justify-center sm:px-0.5 lg:px-1"
      aria-hidden
    >
      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" className="text-stone-300">
        <path
          d="M0 7h14M10 1l6 6-6 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/** Horizontal step cards — stacked on xs, row from sm with CTA rendered separately */
export function HorizontalStepFlow({ steps }: { steps: DemoStep[] }) {
  return (
    <div className="home-hiw-stepper font-sans w-full px-4 sm:px-6 lg:px-10">
      <div className="home-hiw-stepper__row mx-auto flex w-full max-w-7xl flex-col sm:flex-row sm:items-stretch sm:justify-center">
        {steps.map((step, index) => (
          <React.Fragment key={step.num}>
            {index > 0 ? <StepConnector orientation="vertical" /> : null}
            {index > 0 ? <StepConnector orientation="horizontal" /> : null}
            <article className="home-hiw-stepper__card flex min-w-0 flex-1 flex-col sm:max-w-[17rem] lg:max-w-none">
              <div className="flex items-center gap-2.5">
                <span
                  className="home-hiw-stepper__num t-num flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
                  style={{ background: step.color }}
                >
                  {step.num}
                </span>
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{ background: `${step.color}14`, color: step.color }}
                >
                  <step.icon className="h-4 w-4" aria-hidden />
                </div>
              </div>
              <h3 className="t-card-title mt-3 text-balance">{step.title}</h3>
              <p className="t-meta mt-1.5 flex-1 text-balance leading-relaxed">{step.desc}</p>
            </article>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export function MobileStepperFlow({
  onSignup,
  steps,
  signupLabel = 'Start Free — No Card Required',
  showCta = true,
}: {
  onSignup: () => void;
  steps: DemoStep[];
  signupLabel?: string;
  showCta?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const touchActiveRef = useRef(false);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = 0;
  }, []);

  const scrollToStep = useCallback(
    (idx: number) => {
      const el = scrollRef.current;
      if (!el) return;
      const cardWidth = el.scrollWidth / steps.length;
      el.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
    },
    [steps.length],
  );

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / steps.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setActiveIdx(Math.min(idx, steps.length - 1));
  }, [steps.length]);

  useEffect(() => {
    const startAutoPlay = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        if (touchActiveRef.current) return;
        setActiveIdx((prev) => {
          const next = (prev + 1) % steps.length;
          scrollToStep(next);
          return next;
        });
      }, 3000);
    };

    startAutoPlay();
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [scrollToStep, steps.length]);

  const handleTouchStart = () => {
    touchActiveRef.current = true;
  };
  const handleTouchEnd = () => {
    touchActiveRef.current = false;
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-3 mb-4 px-4">
        {steps.map((s, i) => (
          <button
            key={s.num}
            type="button"
            onClick={() => {
              touchActiveRef.current = true;
              scrollToStep(i);
              setTimeout(() => {
                touchActiveRef.current = false;
              }, 5000);
            }}
            className="flex items-center gap-1.5 transition-opacity duration-200"
            style={{ opacity: i === activeIdx ? 1 : 0.4 }}
            aria-label={`Step ${s.num}`}
          >
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
              style={{ background: s.color }}
            >
              {i < activeIdx ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.num}
            </span>
            {i < steps.length - 1 && (
              <div
                className="w-4 h-px"
                style={{ background: i < activeIdx ? s.color : 'var(--ds-border, #e2e8f0)' }}
              />
            )}
          </button>
        ))}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 px-4 scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {steps.map((step) => (
          <div
            key={step.num}
            className="snap-center shrink-0 w-[80vw] max-w-[300px] rounded-2xl p-5 flex flex-col gap-3"
            style={{
              background: 'var(--ds-bg-card, #ffffff)',
              border: '1px solid var(--ds-border, #e2e8f0)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${step.color}14`, color: step.color }}
              >
                <step.icon className="w-5 h-5" />
              </div>
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: `${step.color}14`, color: step.color }}
              >
                Step {step.num}
              </span>
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">{step.title}</h3>
            <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">{step.desc}</p>
          </div>
        ))}
      </div>

      {showCta ? (
        <div className="px-4 pt-2 pb-2">
          <button type="button" onClick={onSignup} className="home-marketing-cta group font-sans w-full">
            {signupLabel}
            <ArrowRight
              className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden
            />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function DesktopStepFlow({ steps }: { steps: DemoStep[] }) {
  return (
    <div className="space-y-5">
      {steps.map((step, i) => (
        <div key={step.num} className="flex gap-4 items-start group">
          <div className="flex flex-col items-center shrink-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm"
              style={{ background: step.color }}
            >
              {step.num}
            </div>
            {i < steps.length - 1 && (
              <div className="w-px h-8 mt-1" style={{ background: 'var(--ds-border, #e2e8f0)' }} />
            )}
          </div>
          <div className="pt-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <step.icon className="w-4 h-4 shrink-0" style={{ color: step.color }} />
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">{step.title}</h3>
            </div>
            <p className="text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
